import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Stack from '@mui/material/Stack/Stack';
import Switch from '@mui/material/Switch/Switch';
import TextField from '@mui/material/TextField/TextField';

import DialogName from '@/shared/types/dialogName.enum';

import AddCashChip from './components/AddCashChip';
import usePaymentForm from './hooks/usePaymentForm';

type PaymentFormProps = {
  paymentFormState: ReturnType<typeof usePaymentForm>;
};

const PaymentForm: FC<PaymentFormProps> = ({ paymentFormState }) => {
  const { t } = useTranslation(['checkout', 'common']);

  const {
    owedReceivedDifference,
    amountReceived,
    cashAmountList,
    amountOwed,
    variant,
    mode,
    onAmountReceivedChange,
    onSubtractFixedAmount,
    onAddFixedAmount,
    onSetMode,
  } = paymentFormState;

  return (
    <Stack sx={{ py: 1 }} spacing={2}>
      <TextField
        disabled
        label={t('checkout:amountOwed')}
        variant='standard'
        defaultValue={amountOwed.toFixed(2)}
      />
      <TextField
        label={t('checkout:amountReceived')}
        variant='standard'
        value={amountReceived}
        inputProps={{ type: 'number' }}
        onChange={(e) => onAmountReceivedChange(e.target.value)}
      />

      {variant === DialogName.CashPayment && (
        <>
          <Stack spacing={2}>
            <Stack direction='row' spacing={1}>
              {cashAmountList
                .filter((amount) => amount >= 1)
                .map((amount) => (
                  <AddCashChip
                    key={amount}
                    iconName='PaymentsRounded'
                    amount={amount}
                    fractionDigits={0}
                    mode={mode}
                    onAddFixedAmount={
                      mode === 'addition'
                        ? onAddFixedAmount
                        : onSubtractFixedAmount
                    }
                  />
                ))}
            </Stack>
            <Stack direction='row' spacing={1}>
              {cashAmountList
                .filter((amount) => amount <= 1)
                .map((amount) => (
                  <AddCashChip
                    key={amount}
                    iconName='PaidRounded'
                    amount={amount}
                    fractionDigits={2}
                    mode={mode}
                    onAddFixedAmount={
                      mode === 'addition'
                        ? onAddFixedAmount
                        : onSubtractFixedAmount
                    }
                  />
                ))}
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  defaultChecked
                  onChange={(e) => onSetMode(e.target.checked)}
                />
              }
              label={
                mode === 'addition'
                  ? t('checkout:addAmount')
                  : t('checkout:subtractAmount')
              }
            />
          </Stack>

          <TextField
            disabled
            label={t('checkout:change')}
            variant='standard'
            value={owedReceivedDifference}
          />
        </>
      )}
    </Stack>
  );
};

export default PaymentForm;
